import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAssetPreloader } from "./hooks/useAssetPreloader";
import LoadingScreen from "./components/Loading/LoadingScreen";
import AboutPage from "./components/Sections/About/AboutPage";
import ContactSection from "./components/Sections/Contact/ContactSection";
import CrazyHeadline from "./components/Sections/Intro/CrazyHeadline";
import TimelineRoadmap from "./components/Sections/Stack/TimelineRoadmap";
import TeamSection from "./components/Sections/Team/TeamSection";
import WorkStackingCards from "./components/Sections/Work/WorkStackingCards";
import Sidebar from "./components/Sidebar";
import Cursor from "./components/Utils/Cursor";
import CursorSpotlight from "./components/Utils/CursorSpotlight";
import InteractiveBlock from "./components/Utils/InteractiveBlock";
import TypewriterChapterMark from "./components/Utils/TypewriterChapterMark";
import "./styles/global.css";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const App = () => {
  const { t } = useTranslation();
  const { isReady, progress } = useAssetPreloader();
  const [currentPage, setCurrentPage] = useState("portfolio");
  const [isFormFocused, setIsFormFocused] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isMessageFocused, setIsMessageFocused] = useState(false);

  // Head animation states
  const [isShocked, setIsShocked] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [isCheerful, setIsCheerful] = useState(false);
  const [, setIsHeadVisible] = useState(true); // Only setter is used

  // Mobile detection state
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 900 : false,
  );

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const exitTimelineRef = useRef(null);

  // Helper to reset all head expression states
  const resetHeadState = () => {
    setIsShocked(false);
    setIsShaking(false);
    setIsCheerful(false);
  };

  useEffect(() => {
    // Animation for the main Interactive Head to exit when approaching Work section
    // and reappear at the end of Work section
    if (currentPage !== "portfolio" || !isReady) return;

    let exitTrigger, reentryTrigger, contactTrigger;
    let exitTl, reentryTl, contactExitTween;
    let blockElement;

    // Wait for DOM to fully paint before setting up ScrollTriggers
    // This ensures section heights are calculated correctly
    const setupTimeoutId = setTimeout(() => {
      blockElement = document.getElementById("interactive-block");
      const workSection = document.getElementById("work");
      const teamSection = document.getElementById("team");
      const contactSection = document.getElementById("contact");

      if (!blockElement || !workSection) return;

      // Set initial state (hidden, ready for entrance animation)
      gsap.set(blockElement, {
        scale: 0,
        y: 100,
        rotateX: 0,
        visibility: "visible",
      });

      // Play entrance animation
      gsap.to(blockElement, {
        scale: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        delay: 0.2, // Small delay after page loads
        onComplete: () => {
          setIsHeadVisible(true);
          setIsCheerful(true);
          setTimeout(() => {
            setIsCheerful(false);
          }, 600);
        },
      });
      resetHeadState();

      // Create the exit timeline (paused, will be triggered by ScrollTrigger)
      exitTl = gsap.timeline({ paused: true });

      exitTl
        // Phase 1: Look down (noticing the section below)
        .add(() => {
          setIsHeadVisible(false); // Mark as not visible (in animation)
        })
        .to(blockElement, { rotateX: 15, duration: 0.3, ease: "power2.out" })
        // Phase 2: Shocked expression (eyes go wide, looking up)
        .add(() => {
          setIsShocked(true);
        })
        .to(blockElement, { rotateX: -10, duration: 0.2, ease: "power2.out" })
        // Phase 3: Start shaking nervously
        .add(() => {
          setIsShaking(true);
        })
        // Phase 4: Shrink while shaking
        .to(
          blockElement,
          { scale: 0, duration: 0.4, ease: "power2.in" },
          "+=0.2",
        )
        // Phase 5: Hide completely
        .set(blockElement, { visibility: "hidden" })
        .add(() => {
          resetHeadState(); // Clear expressions when fully hidden
        });

      // Set up the reverse complete callback once
      exitTl.eventCallback("onReverseComplete", () => {
        // When timeline fully reverses back to start, head is visible again
        setIsHeadVisible(true);
        resetHeadState(); // Clear ALL expressions
        setIsCheerful(true);
        setTimeout(() => {
          setIsCheerful(false);
        }, 600);
      });

      exitTimelineRef.current = exitTl;

      // Create the re-entry timeline
      reentryTl = gsap.timeline({ paused: true });

      reentryTl
        // First reset all states
        .add(() => {
          resetHeadState();
        })
        // Make visible but small and below
        .set(blockElement, { visibility: "visible", scale: 0.3, y: 100 })
        // Slowly rise up and grow
        .to(blockElement, { scale: 1, y: 0, duration: 0.8, ease: "power2.out" })
        // Cheerful wiggle at the end
        .add(() => {
          setIsHeadVisible(true); // Now fully visible
          setIsCheerful(true);
          setTimeout(() => {
            setIsCheerful(false);
          }, 600);
        });

      // ScrollTrigger 1: Exit when entering Work section
      exitTrigger = ScrollTrigger.create({
        trigger: workSection,
        start: "top-=200 center", // Trigger slightly above the Work section
        onEnter: () => {
          exitTl.play();
        },
        onLeaveBack: () => {
          // Re-entry when scrolling back up before Work section
          exitTl.reverse();
          // onReverseComplete callback handles state reset
        },
      });

      // ScrollTrigger 2: Re-entry when entering Team section
      reentryTrigger = ScrollTrigger.create({
        trigger: teamSection,
        start: "top 80%", // Trigger when Team section is 80% from top of viewport
        onEnter: () => {
          // Play re-entry animation
          exitTl.pause();
          reentryTl.restart();
        },
        onLeaveBack: () => {
          // If scrolling back up into Work section, hide again
          resetHeadState(); // Clear any expressions first
          setIsHeadVisible(false);
          gsap.to(blockElement, {
            scale: 0,
            duration: 0.3,
            ease: "power2.in",
            onComplete: () => {
              gsap.set(blockElement, { visibility: "hidden" });
            },
          });
        },
      });

      // ScrollTrigger 3: Fly out to right when entering Contact section
      contactExitTween = gsap.to(blockElement, {
        x: 600,
        duration: 1,
        ease: "power2.inOut",
        paused: true,
      });

      contactTrigger = ScrollTrigger.create({
        trigger: contactSection,
        start: "top 80%", // Trigger when Contact section is 80% from top of viewport
        onEnter: () => {
          contactExitTween.play();
        },
        onLeaveBack: () => {
          contactExitTween.reverse();
        },
      });

      // Refresh ScrollTrigger to recalculate positions after DOM is ready
      ScrollTrigger.refresh();
    }, 100); // Small delay to ensure DOM is painted

    return () => {
      clearTimeout(setupTimeoutId);
      if (exitTrigger) exitTrigger.kill();
      if (reentryTrigger) reentryTrigger.kill();
      if (contactTrigger) contactTrigger.kill();
      if (exitTl) exitTl.kill();
      if (reentryTl) reentryTl.kill();
      if (contactExitTween) contactExitTween.kill();
      if (blockElement) {
        gsap.set(blockElement, {
          scale: 1,
          y: 0,
          x: 0,
          rotateX: 0,
          visibility: "visible",
        });
      }
      setIsHeadVisible(true);
      resetHeadState();
    };
  }, [currentPage, isReady]);

  const handleBackToPortfolio = () => {
    setCurrentPage("portfolio");
  };

  const handleSecretActivate = useCallback(() => {
    setCurrentPage("about");
  }, []);

  // Show loading screen until assets are ready
  if (!isReady) {
    return <LoadingScreen progress={progress} />;
  }

  if (currentPage === "about") {
    return (
      <>
        <Sidebar currentPage={currentPage} setCurrentPage={setCurrentPage} />
        <AboutPage onBack={handleBackToPortfolio} />
      </>
    );
  }

  return (
    <>
      <Sidebar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isFormFocused={isFormFocused}
        isButtonHovered={isButtonHovered}
        isMessageFocused={isMessageFocused}
      />
      <main>
        <section id="hello">
          {/* Hide InteractiveBlock on mobile - 3D rendering not needed for touch devices */}
          {!isMobile && (
            <InteractiveBlock
              onSecretActivate={handleSecretActivate}
              isShocked={isShocked}
              isShaking={isShaking}
              isCheerful={isCheerful}
            />
          )}
          <TypewriterChapterMark text="01 / INTRO" />
          <div style={{ marginTop: "auto", marginBottom: "auto" }}>
            <CrazyHeadline />
            <p className="subtitle">{t("hero.subtitle")}</p>
            <img
              src="/logo_with_text.svg"
              alt="Logo"
              className="intro-logo-mobile"
              style={{ marginTop: "10px", height: "40px" }}
            />
          </div>
        </section>

        <section id="stack">
          <TypewriterChapterMark text="02 / STACK" />

          {/*<div style={{ marginTop: "40px", marginBottom: "20px" }}>
            <h2>The Stack</h2>
            <p
              style={{
                maxWidth: "600px",
                marginTop: "20px",
                color: "rgba(255,255,255,0.5)",
                fontSize: "1rem",
              }}
            >
              Hover to explore the technologies I work with.
            </p>
          </div>

          {/*<StackFlashlight />*/}

          {/* Horizontal scrolling roadmap */}
          <TimelineRoadmap />
        </section>

        <section id="work">
          <TypewriterChapterMark text="03 / WORK" />
          <div className="work-section-wrapper">
            <WorkStackingCards />
          </div>
        </section>

        <section id="team">
          <TypewriterChapterMark text="04 / TEAM" />
          <TeamSection />
        </section>

        <ContactSection
          onFocusChange={setIsFormFocused}
          onMessageFocus={setIsMessageFocused}
          onButtonHover={setIsButtonHovered}
        />
      </main>
      <CursorSpotlight />
      <Cursor />
    </>
  );
};

export default App;
